// Page Sync : bandeau « Tout synchroniser » (pipeline complet), un panneau
// "Fetch raw" par prestataire + un bouton "Régénérer écritures" qui relit la
// couche raw sans toucher aux APIs externes.

import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type ReactNode } from "react";
import { apiGet, apiPost, apiPostForm } from "../lib/api";
import { formatDateFr } from "../lib/format";
import { useDateStore } from "../stores/date-store";
import { PipelineHistory, SyncBanner } from "../components/sync-banner";

export const Route = createFileRoute("/sync")({
  component: SyncPage,
});

type FetchRunStatus = "queued" | "running" | "completed" | "failed" | "interrupted";
interface FetchProgress {
  stage: string;
  current: number;
  total: number | null;
  message: string;
}
type ActiveRun = Pick<FetchRun, "id" | "status" | "progress">;
interface ProviderStatus {
  lastFetchedAt: string | null;
  rawCount: number;
  // Run queued/running du prestataire (au plus un, garanti côté API).
  activeRun: ActiveRun | null;
}
interface StatusResponse {
  providers: Record<string, ProviderStatus>;
}

interface FetchRun {
  id: number;
  provider: string;
  fromDate: string | null;
  toDate: string | null;
  status: FetchRunStatus;
  rowsUpserted: number;
  errorMessage: string | null;
  jobId: string | null;
  progress: FetchProgress | null;
  startedAt: string;
  completedAt: string | null;
}

type FetchProvider = "stripe" | "qonto" | "neon" | "payfit" | "spiko" | "ibkr";
const PROVIDERS: { key: FetchProvider; name: string; unit?: string }[] = [
  { key: "stripe", name: "Stripe" },
  { key: "qonto", name: "Qonto" },
  { key: "neon", name: "Neon (contrats Cartage)" },
  { key: "payfit", name: "Payfit (API, mois de paie clos)", unit: "mois en raw (API et CSV)" },
  { key: "spiko", name: "Spiko (positions, ordres, cours BCE)", unit: "positions-jours en raw" },
  { key: "ibkr", name: "Interactive Brokers (relevé Flex, cours BCE)", unit: "opérations et mouvements de cash en raw" },
];

function isActive(status: FetchRunStatus): boolean {
  return status === "queued" || status === "running";
}

function activeRunLabel(run: ActiveRun): string {
  return run.status === "queued"
    ? `Run #${run.id} en attente…`
    : `Run #${run.id} en cours · ${run.progress?.message ?? "démarrage…"}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function SyncPage() {
  // Fetch et régénération portent sur la période globale (barre en haut de page).
  const { startDate: fromDate, endDate: toDate } = useDateStore();
  const [clearExisting, setClearExisting] = useState(true);
  const queryClient = useQueryClient();

  const { data: status } = useQuery<StatusResponse>({
    queryKey: ["raw-status"],
    queryFn: () => apiGet<StatusResponse>("/api/raw/status"),
    refetchInterval: 5000,
  });

  const { data: runs } = useQuery<FetchRun[]>({
    queryKey: ["fetch-runs"],
    queryFn: () => apiGet<FetchRun[]>("/api/raw/runs?limit=10"),
    refetchInterval: 3000,
  });

  // Retour du callback OAuth Qonto (/api/qonto/callback → /sync?qonto=…).
  const [feedback, setFeedback] = useState<{ kind: "info" | "error"; message: string } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const qonto = params.get("qonto");
    if (qonto === "connected") return { kind: "info", message: "Qonto reconnecté. Vous pouvez relancer le fetch." };
    if (qonto === "error") return { kind: "error", message: `Connexion Qonto échouée : ${params.get("message") ?? "erreur inconnue"}` };
    return null;
  });

  const fetchMutation = useMutation({
    mutationFn: ({ provider }: { provider: FetchProvider }) =>
      apiPost<{ jobId: string; fetchRunId: number; provider: string }>(
        `/api/raw/${provider}/fetch`,
        { fromDate, toDate },
      ),
    onSuccess: (data) => {
      setFeedback({
        kind: "info",
        message: `Fetch ${data.provider} en file d'attente (run #${data.fetchRunId}). L'avancement s'affiche dans l'historique ci-dessous.`,
      });
      queryClient.invalidateQueries({ queryKey: ["fetch-runs"] });
      queryClient.invalidateQueries({ queryKey: ["raw-status"] });
    },
    onError: (err) => setFeedback({ kind: "error", message: `Échec : ${(err as Error).message}` }),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => apiPost("/api/regenerate", { fromDate, toDate, clearExisting }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pnl"] });
      queryClient.invalidateQueries({ queryKey: ["balance-sheet"] });
      queryClient.invalidateQueries({ queryKey: ["financial-summary"] });
      queryClient.invalidateQueries({ queryKey: ["entries"] });
      queryClient.invalidateQueries({ queryKey: ["fetch-runs"] });
    },
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Sync</h1>

      <SyncBanner />

      <div className="text-sm text-gray-500">
        Besoin de ventiler les remboursements d'emprunts ? <a href="/loan-schedules" className="font-medium text-blue-700 underline">Importer les échéanciers comptables</a>.
      </div>

      {feedback && (
        <div
          className={`rounded-lg p-3 text-sm flex items-center justify-between ${
            feedback.kind === "info"
              ? "bg-blue-50 text-blue-800 border border-blue-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          <span>{feedback.message}</span>
          <button onClick={() => setFeedback(null)} className="text-xs underline">
            fermer
          </button>
        </div>
      )}

      <section className="bg-white rounded-lg shadow p-4 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Période</h2>
        <p className="text-sm text-gray-700">
          Fetch et régénération du <strong>{formatDateFr(fromDate)}</strong> au <strong>{formatDateFr(toDate)}</strong>.
          <span className="text-gray-500"> La période se change dans la barre en haut de page.</span>
        </p>
      </section>

      <section className="bg-white rounded-lg shadow p-4 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Fetch raw <span className="text-sm font-normal text-gray-500">— met à jour la couche raw depuis les APIs externes</span>
        </h2>
        <div className="grid gap-3">
          {PROVIDERS.map((p) => (
            <ProviderRow
              key={p.key}
              name={p.name}
              status={status?.providers[p.key]}
              onFetch={() => fetchMutation.mutate({ provider: p.key })}
              isPending={fetchMutation.isPending && fetchMutation.variables?.provider === p.key}
              extra={
                p.key === "qonto" ? (
                  <a href="/api/qonto/auth" className="text-xs text-blue-700 underline">
                    Reconnecter Qonto
                  </a>
                ) : undefined
              }
            />
          ))}
          <CreditAgricoleRow status={status?.providers.ca} />
          <OpeningRow status={status?.providers.opening} />
          <ManualRow status={status?.providers.manual} />
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-4 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">
          Régénérer écritures <span className="text-sm font-normal text-gray-500">— relit la couche raw, sans appel API externe</span>
        </h2>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={clearExisting}
            onChange={(e) => setClearExisting(e.target.checked)}
          />
          Effacer les écritures existantes (les reclassifs manuelles sont préservées)
        </label>
        <button
          onClick={() => regenerateMutation.mutate()}
          disabled={regenerateMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
        >
          {regenerateMutation.isPending ? "Régénération…" : "Régénérer maintenant"}
        </button>
        {regenerateMutation.isError && (
          <div className="text-red-600 text-sm">
            Erreur : {(regenerateMutation.error as Error).message}
          </div>
        )}
      </section>

      <PipelineHistory />

      <section className="bg-white rounded-lg shadow p-4 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Historique des fetches</h2>
        <table className="w-full text-sm">
          <thead className="text-gray-500 text-left">
            <tr>
              <th className="py-1">Provider</th>
              <th>Période</th>
              <th>Statut</th>
              <th className="text-right">Rows</th>
              <th>Début</th>
              <th>Durée</th>
            </tr>
          </thead>
          <tbody>
            {runs?.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-t border-gray-100">
                  <td className="py-1 font-medium">{r.provider}</td>
                  <td>
                    {r.fromDate ?? "—"} → {r.toDate ?? "—"}
                  </td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="text-right tabular-nums">{r.rowsUpserted}</td>
                  <td>{formatDateTime(r.startedAt)}</td>
                  <td>{durationLabel(r.startedAt, r.completedAt)}</td>
                </tr>
                {isActive(r.status) && r.progress && (
                  <tr>
                    <td colSpan={6} className="px-2 pb-2">
                      <ProgressLine progress={r.progress} />
                    </td>
                  </tr>
                )}
                {r.errorMessage && (
                  <tr>
                    <td
                      colSpan={6}
                      className={`px-2 pb-2 text-xs ${
                        r.status === "interrupted" ? "text-amber-800 bg-amber-50" : "text-red-700 bg-red-50"
                      }`}
                    >
                      {r.errorMessage}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!runs?.length && (
              <tr>
                <td colSpan={6} className="py-3 text-center text-gray-400">
                  Aucun run pour le moment
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ProviderRow({
  name,
  status,
  onFetch,
  isPending,
  extra,
  unit = "rows en raw",
}: {
  name: string;
  status: ProviderStatus | undefined;
  onFetch: () => void;
  isPending: boolean;
  extra?: ReactNode;
  unit?: string;
}) {
  const activeRun = status?.activeRun ?? null;
  const busy = isPending || activeRun !== null;
  return (
    <div className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-900">{name}</div>
        <div className="text-xs text-gray-500">
          {status?.rawCount ?? 0} {unit} · dernier fetch : {formatDateTime(status?.lastFetchedAt ?? null)}
          {extra && <> · {extra}</>}
        </div>
        {activeRun && <div className="mt-1 text-xs text-blue-800">{activeRunLabel(activeRun)}</div>}
      </div>
      <button
        onClick={onFetch}
        disabled={busy}
        className="bg-slate-900 hover:bg-slate-700 text-white text-sm px-3 py-1 rounded disabled:opacity-50"
      >
        {busy ? "Fetch…" : "Fetch"}
      </button>
    </div>
  );
}

function ProgressLine({ progress }: { progress: FetchProgress }) {
  const pct =
    progress.total === null
      ? null
      : progress.total === 0
        ? 100
        : Math.min(100, Math.round((progress.current / progress.total) * 100));
  return (
    <div className="space-y-1">
      <div className="text-xs text-gray-600">
        <span className="font-medium">{progress.stage}</span>
        {progress.total !== null && (
          <span className="tabular-nums">
            {" "}
            {progress.current}/{progress.total}
          </span>
        )}
        {" · "}
        {progress.message}
      </div>
      <div className="h-1 w-full bg-gray-100 rounded overflow-hidden">
        <div
          className={`h-full bg-blue-500 ${pct === null ? "w-1/4 animate-pulse" : ""}`}
          style={pct === null ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface CaUploadResult {
  operations: number;
  inserted: number;
  duplicates: number;
  periodFrom: string | null;
  periodTo: string | null;
}

interface OpeningUploadResult {
  year: number;
  lines: number;
  totalCents: number;
}

// Upload d'un CSV vers une route raw : mutation + bouton, partagés par les
// sources sans API (relevé Crédit Agricole, à-nouveaux).
function useCsvUpload<T>(endpoint: () => string) {
  const queryClient = useQueryClient();
  return useMutation<T, Error, File>({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return apiPostForm<T>(endpoint(), fd);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["raw-status"] }),
  });
}

function UploadButton({ label, pending, onFile }: { label: string; pending: boolean; onFile: (f: File) => void }) {
  return (
    <label className="bg-slate-900 hover:bg-slate-700 text-white text-sm px-3 py-1 rounded cursor-pointer whitespace-nowrap">
      {pending ? "Upload…" : label}
      <input
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        disabled={pending}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </label>
  );
}

const CA_EXPORT_URL = "https://espace-client.credit-agricole.fr/ca-cotesdarmor/entreprise/operations-courantes/telechargement-operations";

function CreditAgricoleRow({ status }: { status: ProviderStatus | undefined }) {
  const upload = useCsvUpload<CaUploadResult>(() => "/api/raw/ca/upload");
  const r = upload.data;
  return (
    <div className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-900">Crédit Agricole (relevé CSV)</div>
        <div className="text-xs text-gray-500">
          {status?.rawCount ?? 0} opérations en raw · dernier upload : {formatDateTime(status?.lastFetchedAt ?? null)} ·{" "}
          <a href={CA_EXPORT_URL} target="_blank" rel="noreferrer" className="text-blue-700 underline">
            télécharger le relevé sur l'espace client
          </a>{" "}
          (période libre, les opérations déjà connues sont ignorées)
        </div>
        {r && (
          <div className="mt-1 text-xs text-gray-700">
            {r.operations === 0
              ? "Aucune opération dans ce fichier."
              : `${r.operations} opérations du ${r.periodFrom} au ${r.periodTo} : ${r.inserted} nouvelles, ${r.duplicates} déjà connues. Régénérer pour les comptabiliser.`}
          </div>
        )}
        {upload.isError && <div className="mt-1 text-xs text-red-700">Échec : {upload.error.message}</div>}
      </div>
      <UploadButton label="Uploader un relevé" pending={upload.isPending} onFile={(f) => upload.mutate(f)} />
    </div>
  );
}

function OpeningRow({ status }: { status: ProviderStatus | undefined }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const upload = useCsvUpload<OpeningUploadResult>(() => `/api/raw/opening/upload?year=${encodeURIComponent(year)}`);
  const r = upload.data;
  return (
    <div className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-900">À-nouveaux (bilan d'ouverture, CSV)</div>
        <div className="text-xs text-gray-500">
          {status?.rawCount ?? 0} soldes en raw · dernier upload : {formatDateTime(status?.lastFetchedAt ?? null)} · fichier{" "}
          <span className="font-mono">compte;libelle;debit;credit</span> équilibré, depuis le bilan détaillé de la liasse ; remplace les à-nouveaux de l'exercice
        </div>
        {r && (
          <div className="mt-1 text-xs text-gray-700">
            {r.lines} comptes pour {r.year}, {(r.totalCents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })} de chaque côté. Régénérer depuis le 1er janvier {r.year} pour les comptabiliser.
          </div>
        )}
        {upload.isError && <div className="mt-1 text-xs text-red-700">Échec : {upload.error.message}</div>}
      </div>
      <div className="flex items-center gap-2">
        <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-20 border border-gray-300 rounded px-2 py-1 text-sm" title="Exercice" />
        <UploadButton label="Uploader" pending={upload.isPending} onFile={(f) => upload.mutate(f)} />
      </div>
    </div>
  );
}

function ManualRow({ status }: { status: ProviderStatus | undefined }) {
  return (
    <div className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
      <div>
        <div className="font-medium text-gray-900">Transactions manuelles</div>
        <div className="text-xs text-gray-500">
          {status?.rawCount ?? 0} transactions saisies à la main
        </div>
      </div>
      <a
        href="/manual"
        className="bg-slate-900 hover:bg-slate-700 text-white text-sm px-3 py-1 rounded"
      >
        Gérer
      </a>
    </div>
  );
}

function StatusBadge({ status }: { status: FetchRunStatus }) {
  const color =
    status === "completed"
      ? "bg-green-100 text-green-800"
      : isActive(status)
        ? "bg-blue-100 text-blue-800"
        : status === "failed"
          ? "bg-red-100 text-red-800"
          : status === "interrupted"
            ? "bg-amber-100 text-amber-800"
            : "bg-gray-100 text-gray-800";
  return <span className={`px-2 py-0.5 rounded text-xs ${color}`}>{status}</span>;
}

function durationLabel(start: string, end: string | null): string {
  if (!end) return "…";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}
